// GraphGhostNode — a greyed-out, non-interactive bridge node for a manager who
// sits OUTSIDE the scoped Entra export. The force-graph counterpart of the Org
// Tree's GhostNode: a dashed grey circle (matching PersonNode's shape) with the
// manager's name and a "Not in P&E org" sub-label beneath it.
//
// Synthesised by `augmentWithGhosts` (id prefixed with GHOST_ID_PREFIX) from a
// person's `unresolvedManagerName`. Several reports can share one ghost, which
// re-unifies that sub-org. Ghosts are not clickable, not searchable, not
// counted as people, and have no hover card. See PRODOPSAI-484.

import { truncateLabel } from './utils';
import { GHOST_DASH } from './ghosts';

/** Circle radius — matches PersonNode's REGULAR_RADIUS so ghosts sit visually
 *  level with real nodes. */
const RADIUS = 24;
const NAME_LABEL_OFFSET = 14;
const SUBLABEL_OFFSET = 28;

interface GraphGhostNodeProps {
  /** The unresolved manager's display name. */
  name: string;
  /** Simulated x position (logical SVG coords). */
  x: number;
  /** Simulated y position (logical SVG coords). */
  y: number;
}

/** Stateless, non-interactive ghost-manager node for the force graph. */
export function GraphGhostNode({ name, x, y }: Readonly<GraphGhostNodeProps>) {
  return (
    <g
      transform={`translate(${x}, ${y})`}
      data-testid="org-graph-ghost-node"
      aria-hidden="true"
    >
      <title>{`${name} — Not in P&E org`}</title>
      <circle
        r={RADIUS}
        fill="var(--color-muted)"
        fillOpacity={0.4}
        stroke="var(--color-muted-foreground)"
        strokeOpacity={0.5}
        strokeWidth={1.5}
        strokeDasharray={GHOST_DASH}
        data-testid="org-graph-ghost-circle"
      />
      <text
        y={RADIUS + NAME_LABEL_OFFSET}
        textAnchor="middle"
        className="select-none text-[11px] font-semibold"
        fill="var(--color-muted-foreground)"
      >
        {truncateLabel(name)}
      </text>
      <text
        y={RADIUS + SUBLABEL_OFFSET}
        textAnchor="middle"
        fontStyle="italic"
        className="select-none text-[10px]"
        fill="var(--color-muted-foreground)"
        fillOpacity={0.8}
      >
        Not in P&amp;E org
      </text>
    </g>
  );
}
